import api from '~/api';
import type {
  OnboardingPartnerStatusResponse,
  OnboardingResponse,
  OnboardingStepErrorKeyResponse,
  OnboardingStepErrorTypeResponse,
  OnboardingStepNameResponse,
  OnboardingStepStatusResponse,
} from '../dtos/onboardingResponse';
import { Onboarding } from '../models/onboarding.model';
import { Partner, PartnerStatus } from '../models/partner.model';
import { SalesRepresentative } from '../models/salesRepresentative.model';
import { Step, StepKey, StepStatus } from '../models/step.model';
import { StepError, StepErrorKey, StepErrorType } from '../models/stepError.model';

const StepKeyMap: Record<OnboardingStepNameResponse, StepKey> = {
  complete_partner_details: StepKey.PartnerDetails,
  add_bank_account: StepKey.BankAccount,
  sign_documents: StepKey.SignDocuments,
};

const PartnerStatusMap: Record<OnboardingPartnerStatusResponse, PartnerStatus> = {
  active: PartnerStatus.Active,
  invited: PartnerStatus.Onboarding,
  onboarding: PartnerStatus.Onboarding,
};

const StepStatusMap: Record<OnboardingStepStatusResponse, StepStatus> = {
  pending: StepStatus.Pending,
  completed: StepStatus.Completed,
  blocked: StepStatus.Blocked,
  needs_attention: StepStatus.NeedsAttention,
};

const StepErrorKeyMap: Record<OnboardingStepErrorKeyResponse, StepErrorKey> = {
  identification_number: StepErrorKey.IdentificationNumber,
};

const StepErrorTypeMap: Record<OnboardingStepErrorTypeResponse, StepErrorType> = {
  pending: StepErrorType.Pending,
  invalid: StepErrorType.Invalid,
  duplicated: StepErrorType.Duplicated,
};

export const getOnboarding = async (partnerId: string): Promise<Onboarding> => {
  const fields = [
    'name',
    'onboarding_steps.name',
    'onboarding_steps.order',
    'onboarding_steps.status',
    'onboarding_steps.errors',
    'status',
    'sales_representative.first_name',
    'sales_representative.phone_number',
    'sales_representative.email',
  ];

  const queryParams = new URLSearchParams({
    fields: fields.join(','),
  }).toString();

  const { data } = await api.get<OnboardingResponse>(
    `/v1/partner/${partnerId}/onboarding?${queryParams}`
  );

  const partner = new Partner(data.name, PartnerStatusMap[data.status]);

  const steps = data.onboarding_steps
    .sort((a, b) => a.order - b.order)
    .map((step) => {
      const stepMapped = StepKeyMap[step.name];
      const stepErrors = step.errors.map(
        (error) =>
          new StepError(
            StepErrorKeyMap[error.key],
            error.error_message,
            StepErrorTypeMap[error.type]
          )
      );

      return new Step(stepMapped, step.order, StepStatusMap[step.status], stepErrors);
    });

  const salesRepresentative = new SalesRepresentative(
    data.sales_representative.first_name,
    data.sales_representative.phone_number,
    data.sales_representative.email
  );

  return new Onboarding(partner, steps, salesRepresentative);
};
